import { ArrowUpRight } from "lucide-react";
import { Link } from "react-router-dom";
import { Reveal } from "./Reveal";
import { scrollToId } from "./Header";
import { SERVICES } from "@/data/services";

export const Footer = () => (
  <footer data-testid="site-footer" className="bg-[#0A2333] border-t border-white/10 text-[#F9F8F4]">
    <div className="mx-auto max-w-7xl px-6 md:px-10 py-14 md:py-28">
      <Reveal>
        <button
          data-testid="footer-cta"
          onClick={() => scrollToId("contact")}
          className="group flex flex-wrap items-center gap-6 text-left"
        >
          <span className="font-serif italic text-5xl md:text-7xl tracking-tight transition-colors duration-300 group-hover:text-white">
            Let's talk.
          </span>
          <span className="flex h-14 w-14 md:h-20 md:w-20 items-center justify-center rounded-full border border-white/25 transition-[background-color,color,border-color] duration-300 group-hover:bg-[#F9F8F4] group-hover:text-[#0A2333] group-hover:border-[#F9F8F4]">
            <ArrowUpRight className="h-6 w-6 md:h-8 md:w-8" />
          </span>
        </button>
      </Reveal>

      <Reveal delay={0.15}>
        <p data-testid="footer-tagline" className="mt-10 font-serif italic text-2xl md:text-3xl text-[#A78558]">
          For a brighter tomorrow.
        </p>
      </Reveal>

      <div className="mt-20 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10 border-t border-white/10 pt-12 text-sm">
        <div>
          <div className="flex items-center gap-4">
            <img
              src="/assets/nua-mark-transparent.png"
              alt=""
              aria-hidden="true"
              data-testid="footer-triskele"
              className="h-11 w-auto"
            />
            <div>
              <p className="font-serif text-3xl tracking-wide">NUA</p>
              <p className="mt-1 text-[10px] uppercase tracking-[0.4em] text-[#A78558]">Private Wealth</p>
            </div>
          </div>
          <p className="mt-4 text-white leading-relaxed max-w-xs">
            Nua is Irish for “new, fresh and modern” — independent financial advice for
            Canberra. Privately owned, fee-for-service, and answerable only to our clients.
          </p>
          <div className="mt-8 border-t border-white/10 pt-6" data-testid="footer-triskele-meaning">
            <p className="text-[10px] uppercase tracking-[0.25em] text-[#A78558]">The Triskelion</p>
            <p className="mt-3 text-sm text-white leading-relaxed max-w-xs">
              An ancient Irish symbol of new beginnings, progress and continuous growth.
              It represents the past, present and future — a fitting reflection of our
              commitment to helping you build and preserve wealth for generations to come.
            </p>
          </div>
        </div>
        <div>
          <p className="text-[10px] uppercase tracking-[0.25em] text-white">Contact</p>
          <ul className="mt-2 md:mt-4 md:space-y-2 text-white">
            <li><a data-testid="footer-email-link" href="mailto:hello@nuaprivatewealth.com.au" className="inline-block py-2.5 md:py-0 hover:text-white transition-colors duration-300">hello@nuaprivatewealth.com.au</a></li>
            <li><a data-testid="footer-phone-link" href="tel:+61411842648" className="inline-block py-2.5 md:py-0 hover:text-white transition-colors duration-300">0411 842 648</a></li>
            <li className="py-2.5 md:py-0">Office 845, Level 8, 121 Marcus Clarke Street, Canberra ACT 2601</li>
          </ul>
        </div>
        <div>
          <p className="text-[10px] uppercase tracking-[0.25em] text-white">Services</p>
          <ul className="mt-2 md:mt-4 md:space-y-2 text-white">
            {SERVICES.map((s) => (
              <li key={s.slug}>
                <Link
                  data-testid={`footer-service-${s.slug}`}
                  to={`/services/${s.slug}`}
                  className="inline-block py-2.5 md:py-0 hover:text-white transition-colors duration-300"
                >
                  {s.title}
                </Link>
              </li>
            ))}
          </ul>
        </div>
        <div>
          <p className="text-[10px] uppercase tracking-[0.25em] text-white">Explore</p>
          <ul className="mt-2 md:mt-4 md:space-y-2 text-white">
            <li><button data-testid="footer-link-charter" onClick={() => scrollToId("charter")} className="inline-block py-2.5 md:py-0 hover:text-white transition-colors duration-300">The Independence Charter</button></li>
            <li><button data-testid="footer-link-services" onClick={() => scrollToId("services")} className="inline-block py-2.5 md:py-0 hover:text-white transition-colors duration-300">Services</button></li>
            <li><button data-testid="footer-link-about" onClick={() => scrollToId("about")} className="inline-block py-2.5 md:py-0 hover:text-white transition-colors duration-300">About your adviser</button></li>
            <li><Link data-testid="footer-link-online-advice" to="/online-advice" className="inline-block py-2.5 md:py-0 hover:text-white transition-colors duration-300">Online advice</Link></li>
            <li><Link data-testid="footer-link-fee-check" to="/advice-fee-health-check" className="inline-block py-2.5 md:py-0 hover:text-white transition-colors duration-300">Fee Health Check</Link></li>
            <li><Link data-testid="footer-link-fact-find" to="/fact-find" className="inline-block py-2.5 md:py-0 hover:text-white transition-colors duration-300">Client Fact Find</Link></li>
            <li><Link data-testid="footer-link-client-upload" to="/client-documents" className="inline-block py-2.5 md:py-0 hover:text-white transition-colors duration-300">Client document upload</Link></li>
            <li><button data-testid="footer-link-contact" onClick={() => scrollToId("contact")} className="inline-block py-2.5 md:py-0 hover:text-white transition-colors duration-300">Book a free consultation</button></li>
          </ul>
        </div>
      </div>

      <div className="mt-16 border-t border-white/10 pt-8 flex flex-col md:flex-row justify-between gap-6">
        <p className="text-xs text-white" data-testid="footer-copyright">
          © 2026 NUA Private Wealth Pty Ltd. Canberra, ACT. <a href="/admin/login" data-testid="footer-admin-login" className="inline-block py-3 underline underline-offset-4 hover:text-white transition-colors duration-300">Adviser login</a>
        </p>
        <p className="text-xs leading-relaxed text-white max-w-xl" data-testid="footer-disclaimer">
          The information on this website is general in nature and does not take into account
          your objectives, financial situation or needs. Before acting on any information,
          consider its appropriateness to your circumstances.
        </p>
      </div>
    </div>
  </footer>
);
